import React, { useEffect, useState } from "react";
import "./style.css";
import { Empty } from "antd";
import { getDashboard } from "../../../API/dashboard";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import LineChart from "../../../Components/Charts/LineChart";
import BarChart from "../../../Components/Charts/BarChart";
import AreaChart from "../../../Components/Charts/AreaChart";
import GenericTable from "../../../Components/GenericTable";
import Lottie from "lottie-react";
import reportGeneration from "../../../Assets/Animations/Report Generation.json";
import { BATTERY_UI_MAP } from "../../../Utils/BatteryStateMap";

const defaultDashboardData = {
  idlevsmovement: {},
  total_idle_hours: "0",
  total_engine_hours: "0",
  avg_mileage: {},
  avg_fuel_consumption: {},
  total_fuel_wasted: {},
  wasted_perc: 0,
  used_perc: 0,
  total_added_fuel: {},
  added_fuel_total: 0,
  fuel_removed_stationary: {},
  fuel_removed_stationary_total: 0,
  total_fuel_theft: {},
  fuel_theft_total: 0,
  added_fuel_total: 0,
  door_open_close: [],
  total_door_counts: 0,
  total_movement_hours: "0",
  idle_cost: "0",
  engine_cost: "0",
  total_cost: "0",
  upcoming_services: [],
  idle_percentage: 0,
  fleet_active_percentage: 0,
  board_voltage_data: [],
  vehicle_based_services: [],
  scheduled_based_services: [],
};

const Dashboard = () => {
  const { from, to } = useDateFilter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(defaultDashboardData);

  useEffect(() => {
    if (!to || !from) {
      return;
    }

    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getDashboard(to, from);
        setDashboardData(response.data || defaultDashboardData);
      } catch (error) {
        console.error("Failed to fetch dashboard data", error);
        setError("Failed to load dashboard data. Please try again later.");
        setDashboardData(defaultDashboardData);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [to, from]);

  if (error) {
    return (
      <div className="dashboard">
        <div
          style={{
            height: "80vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Empty description={error} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      </div>
    );
  }
  const voltageDataColumns = [
    {
      title: <span className="vehicle-title">Vehicle Name</span>,
      dataIndex: "name",
      key: "name",
      sorter: (a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Board Voltage (V)",
      dataIndex: "board_voltage",
      key: "board_voltage",
      sorter: (a, b) => a.board_voltage - b.board_voltage,
    },
    {
      title: <span className="vehicle-title">Percentage (SOC)</span>,
      dataIndex: "soc_percentage",
      key: "soc_percentage",
      render: (percentage) => <span>{percentage}%</span>,
      sorter: (a, b) =>
        a.soc_percentage.localeCompare(b.soc_percentage, undefined, {
          numeric: true,
        }),
    },
    {
      title: <span className="vehicle-title">Battery Voltage</span>,
      dataIndex: "battery_voltage",
      key: "battery_voltage",
      sorter: (a, b) =>
        a.battery_voltage.localeCompare(b.battery_voltage, undefined, {
          numeric: true,
        }),
    },
    {
      title: <span className="vehicle-title">Ignition State</span>,
      dataIndex: "ignition",
      key: "ignition",
      render: (ignition) => {
        const isOn = ignition === "On";
        return (
          <span className={isOn ? "text-green-class" : "text-red-class"}>
            {ignition ?? "Unknown"}
          </span>
        );
      },
      sorter: (a, b) =>
        a.ignition.localeCompare(b.ignition, undefined, {
          numeric: true,
        }),
    },
    {
      title: <span className="vehicle-title">Battery State</span>,
      dataIndex: "battery_recomendation",
      key: "battery_recomendation",
      render: (value) => {
        const ui = BATTERY_UI_MAP[value] || BATTERY_UI_MAP.unknown;

        return (
          <span className={`battery-state ${ui.className}`}>
            <span className={`status-dot ${ui.dot}`} />
            {ui.label}
          </span>
        );
      },
      sorter: (a, b) =>
        a.battery_recomendation.localeCompare(b.battery_recomendation, undefined, {
          numeric: true,
        }),
    },
  ];

  return (
    <div className="dashboard">
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="loader-container"
        >
          <Lottie
            animationData={reportGeneration}
            loop={true}
            style={{ width: 150, height: 150 }}
          />
        </div>
      ) : (
        <div className="rows-container">
          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Idle Hours Cost for Fleet</span>
                <span className="hours">
                  {dashboardData?.total_idle_hours || "0"}
                </span>

                <span className="cost" style={{ color: "#DFB400" }}>
                  ${dashboardData?.idle_cost}
                </span>
                <span className="tab-sub-title">
                  Idle accounts for {dashboardData?.idle_percentage}% of engine
                  time
                </span>
              </div>
              <div className="right">
                <div className="time">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                  >
                    <circle
                      opacity="0.5"
                      cx="17"
                      cy="18.4167"
                      r="12.75"
                      stroke="#2662D9"
                      stroke-width="2"
                    />
                    <path
                      d="M17 12.75V18.4167L20.5417 21.9583"
                      stroke="#2662D9"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M4.95831 6.375L10.625 2.83333"
                      stroke="#2662D9"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M29.0417 6.375L23.375 2.83333"
                      stroke="#2662D9"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div className="graphic">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="109"
                    height="59"
                    viewBox="0 0 109 59"
                    fill="none"
                  >
                    <path
                      d="M0.0251465 47.3188C19.1568 24.771 38.2885 2.22239 57.4201 0.188151C76.5518 -1.84682 95.6834 16.6326 114.815 29.3737C133.947 42.1149 153.078 49.1186 172.21 47.9104C191.342 46.703 210.473 37.2829 229.605 32.9234C248.737 28.5639 267.868 29.2642 287 29.9653V72.1662C267.868 74.9214 248.737 77.6766 229.605 74.1387C210.473 70.6001 191.342 60.7684 172.21 62.3066C153.078 63.8441 133.947 76.7521 114.815 76.1105C95.6834 75.4689 76.5518 61.2777 57.4201 60.3341C38.2885 59.3905 19.1568 71.6944 0.0251465 83.9983V47.3188Z"
                      fill="url(#paint0_linear_242_22111)"
                      fill-opacity="0.4"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_242_22111"
                        x1="59.9998"
                        y1="0.000182288"
                        x2="60.9998"
                        y2="58.5002"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#2662D9" stop-opacity="0.8" />
                        <stop
                          offset="1"
                          stop-color="#2662D9"
                          stop-opacity="0.1"
                        />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Movement Hours Cost For Fleet</span>
                <span className="hours">
                  {dashboardData?.total_movement_hours || "0"}
                </span>
                <span className="cost">
                  ${dashboardData?.total_cost || "0"}
                </span>
                <span className="tab-sub-title">
                  For time duration selected
                </span>
              </div>
              <div className="right">
                <div className="time">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                  >
                    <path
                      d="M2.83337 14.1667C2.83337 10.1597 2.83337 8.15626 4.07817 6.91147C5.32297 5.66667 7.32644 5.66667 11.3334 5.66667H18.4167C22.4236 5.66667 24.4271 5.66667 25.6719 6.91147C26.9167 8.15626 26.9167 10.1597 26.9167 14.1667C26.9167 18.1736 26.9167 20.1771 25.6719 21.4219C24.4271 22.6667 22.4236 22.6667 18.4167 22.6667H11.3334C7.32644 22.6667 5.32297 22.6667 4.07817 21.4219C2.83337 20.1771 2.83337 18.1736 2.83337 14.1667Z"
                      stroke="#DFB400"
                      stroke-width="2"
                    />
                    <path
                      opacity="0.5"
                      d="M26.917 10.0246C28.2984 10.1605 29.2282 10.4675 29.9222 11.1615C31.167 12.4063 31.167 14.4098 31.167 18.4167C31.167 22.4237 31.167 24.4272 29.9222 25.6719C28.6774 26.9167 26.674 26.9167 22.667 26.9167H15.5837C11.5768 26.9167 9.57329 26.9167 8.3285 25.6719C7.63448 24.9779 7.3274 24.0481 7.19153 22.6667"
                      stroke="#DFB400"
                      stroke-width="2"
                    />
                    <path
                      d="M18.4167 14.1667C18.4167 16.1227 16.831 17.7083 14.875 17.7083C12.919 17.7083 11.3334 16.1227 11.3334 14.1667C11.3334 12.2107 12.919 10.625 14.875 10.625C16.831 10.625 18.4167 12.2107 18.4167 14.1667Z"
                      stroke="#DFB400"
                      stroke-width="2"
                    />
                    <path
                      opacity="0.5"
                      d="M22.6666 17L22.6666 11.3333"
                      stroke="#DFB400"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                    <path
                      opacity="0.5"
                      d="M7.08337 17L7.08337 11.3333"
                      stroke="#DFB400"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>
                <div className="graphic"></div>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Engine Hours Cost for Fleet</span>
                <span className="hours">
                  {dashboardData?.total_engine_hours || "0"}
                </span>
                <span className="cost">
                  ${dashboardData?.engine_cost || "0"}
                </span>
                <span className="tab-sub-title">
                  For time duration selected
                </span>
              </div>
              <div className="right">
                <div className="time">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                  >
                    <circle
                      opacity="0.5"
                      cx="17"
                      cy="18.4167"
                      r="12.75"
                      stroke="#2662D9"
                      stroke-width="2"
                    />
                    <path
                      d="M17 12.75V18.4167L20.5417 21.9583"
                      stroke="#2662D9"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M4.95831 6.375L10.625 2.83333"
                      stroke="#2662D9"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M29.0417 6.375L23.375 2.83333"
                      stroke="#2662D9"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div className="graphic">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="109"
                    height="59"
                    viewBox="0 0 109 59"
                    fill="none"
                  >
                    <path
                      d="M-59.9749 47.3188C-40.8432 24.771 -21.7115 2.22239 -2.57988 0.188151C16.5518 -1.84682 35.6834 16.6326 54.8151 29.3737C73.9467 42.1149 93.0784 49.1186 112.21 47.9104C131.342 46.703 150.473 37.2829 169.605 32.9234C188.737 28.5639 207.868 29.2642 227 29.9653V72.1662C207.868 74.9214 188.737 77.6766 169.605 74.1387C150.473 70.6001 131.342 60.7684 112.21 62.3066C93.0784 63.8441 73.9467 76.7521 54.8151 76.1105C35.6834 75.4689 16.5518 61.2777 -2.57988 60.3341C-21.7115 59.3905 -40.8432 71.6944 -59.9749 83.9983V47.3188Z"
                      fill="url(#paint0_linear_242_22125)"
                      fill-opacity="0.4"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_242_22125"
                        x1="-0.000242654"
                        y1="0.000182288"
                        x2="0.999757"
                        y2="58.5002"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stop-color="#2662D9" stop-opacity="0.8" />
                        <stop
                          offset="1"
                          stop-color="#2662D9"
                          stop-opacity="0.1"
                        />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Active %</span>
                <span className="hours" style={{ color: "#1FC16B" }}>
                  {dashboardData?.fleet_active_percentage}%
                </span>
                <span
                  className="cost"
                  style={{
                    color: "var(--Parimitives-Charts-dashboard-card-BG)",
                  }}
                >
                  .
                </span>
                <span className="tab-sub-title">
                  Based on running engine hours
                </span>
              </div>
              <div className="right">
                <div className="time">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                  >
                    <path
                      opacity="0.5"
                      d="M13.8569 5.51122C14.6557 4.83047 15.0551 4.4901 15.4727 4.2905C16.4386 3.82885 17.5614 3.82885 18.5273 4.2905C18.9449 4.4901 19.3443 4.83047 20.1432 5.51122C20.4611 5.78216 20.62 5.91763 20.7898 6.03142C21.179 6.29227 21.6161 6.47331 22.0757 6.56405C22.2762 6.60364 22.4844 6.62025 22.9008 6.65348C23.947 6.73697 24.4701 6.77871 24.9066 6.93286C25.916 7.2894 26.7099 8.08338 27.0665 9.0928C27.2206 9.52922 27.2624 10.0523 27.3459 11.0985C27.3791 11.5149 27.3957 11.7231 27.4353 11.9237C27.526 12.3833 27.7071 12.8203 27.9679 13.2095C28.0817 13.3793 28.2172 13.5383 28.4881 13.8562C29.1689 14.655 29.5092 15.0544 29.7088 15.472C30.1705 16.4379 30.1705 17.5608 29.7088 18.5266C29.5092 18.9442 29.1689 19.3436 28.4881 20.1425C28.2172 20.4604 28.0817 20.6194 27.9679 20.7891C27.7071 21.1783 27.526 21.6154 27.4353 22.075C27.3957 22.2755 27.3791 22.4837 27.3459 22.9001C27.2624 23.9463 27.2206 24.4694 27.0665 24.9059C26.7099 25.9153 25.916 26.7093 24.9066 27.0658C24.4701 27.2199 23.947 27.2617 22.9008 27.3452C22.4844 27.3784 22.2762 27.395 22.0757 27.4346C21.6161 27.5254 21.179 27.7064 20.7898 27.9672C20.62 28.081 20.4611 28.2165 20.1432 28.4874C19.3443 29.1682 18.9449 29.5086 18.5273 29.7082C17.5614 30.1698 16.4386 30.1698 15.4727 29.7082C15.0551 29.5086 14.6557 29.1682 13.8569 28.4874C13.539 28.2165 13.38 28.081 13.2102 27.9672C12.821 27.7064 12.384 27.5254 11.9243 27.4346C11.7238 27.395 11.5156 27.3784 11.0992 27.3452C10.053 27.2617 9.52991 27.2199 9.09348 27.0658C8.08407 26.7093 7.29009 25.9153 6.93355 24.9059C6.7794 24.4694 6.73766 23.9463 6.65417 22.9001C6.62094 22.4837 6.60432 22.2755 6.56474 22.075C6.47399 21.6154 6.29296 21.1783 6.03211 20.7891C5.91832 20.6194 5.78285 20.4604 5.51191 20.1425C4.83116 19.3436 4.49079 18.9442 4.29119 18.5266C3.82953 17.5608 3.82953 16.4379 4.29119 15.472C4.49079 15.0544 4.83116 14.655 5.51191 13.8562C5.78285 13.5383 5.91832 13.3793 6.03211 13.2095C6.29296 12.8203 6.47399 12.3833 6.56474 11.9236C6.60432 11.7231 6.62094 11.5149 6.65417 11.0985C6.73766 10.0523 6.7794 9.52922 6.93355 9.0928C7.29009 8.08338 8.08407 7.2894 9.09348 6.93286C9.52991 6.77871 10.053 6.73697 11.0992 6.65348C11.5156 6.62025 11.7238 6.60364 11.9243 6.56405C12.384 6.47331 12.821 6.29227 13.2102 6.03142C13.38 5.91763 13.539 5.78216 13.8569 5.51122Z"
                      stroke="#1FC16B"
                      stroke-width="2"
                    />
                    <path
                      d="M12.75 21.25L21.25 12.75"
                      stroke="#1FC16B"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                    <path
                      d="M21.9583 20.5417C21.9583 21.3241 21.3241 21.9583 20.5417 21.9583C19.7593 21.9583 19.125 21.3241 19.125 20.5417C19.125 19.7593 19.7593 19.125 20.5417 19.125C21.3241 19.125 21.9583 19.7593 21.9583 20.5417Z"
                      fill="#1FC16B"
                    />
                    <path
                      d="M14.875 13.4583C14.875 14.2407 14.2407 14.875 13.4583 14.875C12.6759 14.875 12.0416 14.2407 12.0416 13.4583C12.0416 12.6759 12.6759 12.0417 13.4583 12.0417C14.2407 12.0417 14.875 12.6759 14.875 13.4583Z"
                      fill="#1FC16B"
                    />
                  </svg>
                </div>
                <div className="graphic" style={{ margin: "12px" }}>
                  <svg
                    width="19"
                    height="73"
                    viewBox="0 0 19 73"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18.3455 17.9945C18.3455 17.246 18.0481 16.5281 17.5188 15.9988C16.9895 15.4695 16.2716 15.1721 15.5231 15.1721H2.82239C2.07385 15.1721 1.35596 15.4695 0.826668 15.9988C0.297369 16.5281 1.14441e-05 17.246 1.14441e-05 17.9945V69.1776C1.14441e-05 69.9262 0.297369 70.6441 0.826668 71.1734C1.35596 71.7027 2.07385 72 2.82239 72H15.5231C16.2716 72 16.9895 71.7027 17.5188 71.1734C18.0481 70.6441 18.3455 69.9262 18.3455 69.1776V17.9945Z"
                      fill="#1FC16B"
                    />
                    <path
                      d="M18.3457 3.59187C18.3457 2.63925 18.0483 1.72564 17.519 1.05204C16.9897 0.378428 16.2719 0 15.5233 0H2.82263C2.07409 0 1.35621 0.378428 0.82691 1.05204C0.297611 1.72564 0.000255585 2.63925 0.000255585 3.59187V68.7297C0.000255585 69.6823 0.297611 70.5959 0.82691 71.2695C1.35621 71.9431 2.07409 72.3216 2.82263 72.3216H15.5233C16.2719 72.3216 16.9897 71.9431 17.519 71.2695C18.0483 70.5959 18.3457 69.6823 18.3457 68.7297V3.59187Z"
                      fill="#1FC16B"
                      fill-opacity="0.1"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <LineChart
                graphData={dashboardData?.avg_fuel_consumption || {}}
                lengthKeys={2}
                colourTheme={["#2662D9", "#B9CEFE"]}
                graphTitle="Avg. Fuel Consumption"
                selectTenByDefault={false}
                classNameCSS="avg-fuel-consumption"
                units={["L/hr", "L/km"]}
              />
            </div>
            <div className="graph">
              <LineChart
                graphData={dashboardData?.total_fuel_wasted || {}}
                lengthKeys={2}
                colourTheme={["#2662D9", "#FB3748"]}
                graphTitle="Total Fuel Wasted"
                selectTenByDefault={false}
                classNameCSS="total-fuel-wasted"
                units={["L", "L"]}
              />
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <AreaChart
                graphData={dashboardData?.idlevsmovement || {}}
                length={2}
                colourTheme={["#2662D9", "#1FC16B"]}
                graphTitle="Idle Vs Movement"
                selectTenByDefault={true}
                units={["%", "%"]}
                classNameCSS="idle-movement"
              />
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <BarChart
                graphData={dashboardData?.total_added_fuel || {}}
                lengthKeys={1}
                colourTheme={["#2662D9"]}
                graphTitle="Total Fuel Added"
                selectTenByDefault={false}
                units={["L"]}
                classNameCSS="total-fuel-added"
              />
            </div>

            <div className="graph">
              <BarChart
                graphData={dashboardData?.fuel_removed_stationary || {}}
                lengthKeys={1}
                colourTheme={["#FB3748"]}
                graphTitle="Fuel Removed from Stationary Tank"
                selectTenByDefault={false}
                units={["L"]}
                classNameCSS="fuel-removed"
              />
            </div>
          </div>
          <div className="row">
            <div className="graph">
              <BarChart
                graphData={dashboardData?.avg_mileage || {}}
                lengthKeys={1}
                colourTheme={["#1FC16B"]}
                graphTitle="Total Mileage"
                selectTenByDefault={false}
                units={["Km"]}
                classNameCSS="total-mileage"
              />
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <LineChart
                graphData={dashboardData?.total_fuel_theft || {}}
                lengthKeys={1}
                colourTheme={["#FB3748"]}
                graphTitle="Total Fuel Theft from Vehicle"
                selectTenByDefault={false}
                units={["L"]}
                classNameCSS="fuel-theft-from-vehicle"
              />
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <BarChart
                graphData={dashboardData?.vehicle_based_services || {}}
                lengthKeys={1}
                colourTheme={["#1FC16B"]}
                graphTitle="Average Servicing Spending Per Unit"
                selectTenByDefault={false}
                units={["$"]}
                classNameCSS="total-mileage"
              />
            </div>
            <div className="graph">
              <BarChart
                graphData={dashboardData?.scheduled_based_services || {}}
                lengthKeys={1}
                colourTheme={["#1FC16B"]}
                graphTitle="Total Spending On Fleet"
                selectTenByDefault={false}
                units={["$"]}
                classNameCSS="total-mileage"
              />
            </div>
          </div>
          <GenericTable
            title="Board Voltage"
            dataSource={dashboardData?.board_voltage_data}
            columns={voltageDataColumns}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
